"use client";

import { c as _c } from "react/compiler-runtime";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight, Users, IndianRupee, CalendarCheck, TrendingUp } from "lucide-react";
import { Card } from "@/shared/components/ui/card";

// Small KPI tiles shown across the top of the mock dashboard.
const KPIS = [{
  icon: Users,
  label: "Active members",
  value: "1,284",
  delta: "+6.2%"
}, {
  icon: IndianRupee,
  label: "Revenue (Jun)",
  value: "₹4.8L",
  delta: "+12%"
}, {
  icon: CalendarCheck,
  label: "Check-ins today",
  value: "317",
  delta: "+3.1%"
}];

// Heights for the faux revenue-vs-expense bar chart, in percentages.
const BARS = [42, 58, 51, 70, 64, 82, 76, 91];

// A coded, on-brand mini dashboard — no screenshots, all markup.
export const HeroDashboardMock = () => {
  const $ = _c(11);
  const reduce = useReducedMotion();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex items-center gap-2 border-b border-border/70 px-4 py-3"><span className="size-2.5 rounded-full bg-destructive/40" /><span className="size-2.5 rounded-full bg-amber-400/50" /><span className="size-2.5 rounded-full bg-primary/60" /><span className="ml-3 text-xs font-medium text-muted-foreground">yourgym.gymmart.app/dashboard</span></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="grid grid-cols-3 gap-3">{KPIS.map(_temp)}</div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <p className="text-sm font-semibold">Revenue vs expenses</p>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="mb-3 flex items-center justify-between">{t2}<span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600"><TrendingUp className="size-3.5" /> Trending up</span></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== reduce) {
    t4 = BARS.map((h, i) => <motion.div key={i} className="flex-1 rounded-t-md bg-gradient-to-t from-primary/40 to-primary" initial={reduce ? false : {
      height: 0
    }} whileInView={{
      height: `${h}%`
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.6,
      delay: 0.4 + i * 0.06,
      ease: "easeOut"
    }} />);
    $[4] = reduce;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t4) {
    t5 = <div className="rounded-xl border border-border/60 bg-background/60 p-4">{t3}<div className="flex h-28 items-end gap-2">{t4}</div></div>;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="space-y-2">{[{
        name: "Priya Sharma",
        note: "Renewed \xB7 Gold plan",
        amt: "\u20B93,000"
      }, {
        name: "Arjun Mehta",
        note: "Checked in \xB7 7:42 AM",
        amt: "\u2014"
      }].map(_temp2)}</div>;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t5) {
    t7 = <Card className="w-full gap-0 py-0 shadow-2xl shadow-primary/10">{t0}<div className="space-y-4 p-4 sm:p-5">{t1}{t5}{t6}</div></Card>;
    $[9] = t5;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  return t7;
};
function _temp(kpi) {
  return <div key={kpi.label} className="rounded-xl border border-border/60 bg-background/60 p-3"><kpi.icon className="size-4 text-primary" /><p className="mt-2 text-base font-bold tracking-tight sm:text-lg">{kpi.value}</p><p className="truncate text-[11px] text-muted-foreground">{kpi.label}</p><span className="mt-1 inline-flex items-center gap-0.5 text-[11px] font-semibold text-emerald-600"><ArrowUpRight className="size-3" />{kpi.delta}</span></div>;
}
function _temp2(row) {
  return <div key={row.name} className="flex items-center justify-between rounded-lg border border-border/50 bg-background/40 px-3 py-2"><div className="flex items-center gap-2.5"><span className="flex size-7 items-center justify-center rounded-full bg-primary/15 text-[11px] font-bold text-primary">{row.name.charAt(0)}</span><div className="leading-tight"><p className="text-xs font-semibold">{row.name}</p><p className="text-[11px] text-muted-foreground">{row.note}</p></div></div><span className="text-xs font-semibold text-muted-foreground">{row.amt}</span></div>;
}